import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    ListTodo, Plus, Loader2, Star, User, Truck, Search, Trash2, Clock, ArrowRight, Eye, ArrowLeft,
    Paperclip, Upload, X, Download, FileText, Pencil, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_LABELS = {
    nouvelle: 'Nouvelle',
    en_traitement: 'En traitement',
    termine: 'Terminé',
    echoue: 'Échoué',
};

const STATUT_COLORS = {
    nouvelle: 'bg-blue-100 text-blue-700 border-blue-200',
    en_traitement: 'bg-amber-100 text-amber-700 border-amber-200',
    termine: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    echoue: 'bg-red-100 text-red-700 border-red-200',
};

const STATUT_TRANSITIONS = {
    nouvelle: ['en_traitement', 'termine', 'echoue'],
    en_traitement: ['termine', 'echoue'],
    termine: [],
    echoue: ['en_traitement'],
};

// Couleurs par type/rôle d'assigné
const ASSIGNEE_COLORS = {
    admin: 'bg-rose-100 text-rose-700 border-rose-200',
    commercial: 'bg-blue-100 text-blue-700 border-blue-200',
    assistante: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    demenageur: 'bg-violet-100 text-violet-700 border-violet-200',
    default: 'bg-slate-100 text-slate-700 border-slate-200',
};

const ROLE_LABELS = {
    admin: 'Admin',
    commercial: 'Commercial',
    assistante: 'Assistante',
    demenageur: 'Déménageur',
};

function formatDate(d) { if (!d) return '-'; try { return format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch { return d; } }

export default function TachesAEffectuerPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [demenageurs, setDemenageurs] = useState([]);

    const [search, setSearch] = useState('');
    const [filtreCreePar, setFiltreCreePar] = useState('tous');
    const [filtreAssignee, setFiltreAssignee] = useState('tous');
    const [filtreStatut, setFiltreStatut] = useState('tous');

    const [showCreate, setShowCreate] = useState(false);
    const [createDesc, setCreateDesc] = useState('');
    const [createAssignee, setCreateAssignee] = useState('');
    const [creating, setCreating] = useState(false);
    // Fichiers joints à la création (photos / PDF)
    const [createFiles, setCreateFiles] = useState([]);
    const createFileInputRef = useRef(null);
    const MAX_FILES = 5;
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo

    const [statutModal, setStatutModal] = useState(null); // { tache, target }
    const [statutRemarque, setStatutRemarque] = useState('');
    const [statutSaving, setStatutSaving] = useState(false);

    const [historyTache, setHistoryTache] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [detailTache, setDetailTache] = useState(null);
    const [editingDescription, setEditingDescription] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState('');
    const [savingDescription, setSavingDescription] = useState(false);
    const detailFileInputRef = useRef(null);
    const [uploadingFile, setUploadingFile] = useState(false);

    const fetchUsers = async () => {
        try {
            const { data } = await axios.get(`${API}/api/taches-effectuer/users`, { withCredentials: true });
            setUsers(data.users || []);
            setDemenageurs(data.demenageurs || []);
        } catch { /* ignore */ }
    };

    const fetchItems = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/taches-effectuer`, { withCredentials: true });
            setItems(data || []);
        } catch {
            toast.error('Erreur de chargement');
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchUsers(); fetchItems(); }, []);

    const allAssignables = useMemo(
        () => [
            ...users,
            ...(demenageurs.length > 0 ? [{ id: 'demenageur', nom: 'Déménageur', role: 'demenageur', type: 'demenageur' }] : []),
        ],
        [users, demenageurs],
    );

    // Filtrage côté client
    const STATUT_ORDER = { nouvelle: 0, en_traitement: 1, termine: 2, echoue: 3 };
    const filtered = items.filter(t => {
        if (filtreCreePar !== 'tous' && t.cree_par_id !== filtreCreePar) return false;
        if (filtreAssignee !== 'tous' && t.assignee_id !== filtreAssignee) return false;
        if (filtreStatut !== 'tous' && t.statut !== filtreStatut) return false;
        if (search) {
            const s = search.toLowerCase();
            return (
                (t.description || '').toLowerCase().includes(s)
                || (t.cree_par_nom || '').toLowerCase().includes(s)
                || (t.assignee_nom || '').toLowerCase().includes(s)
            );
        }
        return true;
    }).sort((a, b) => {
        const oa = STATUT_ORDER[a.statut] ?? 99;
        const ob = STATUT_ORDER[b.statut] ?? 99;
        if (oa !== ob) return oa - ob;
        return (b.created_at || '').localeCompare(a.created_at || '');
    });

    // Création
    const handleCreateFiles = (e) => {
        const incoming = Array.from(e.target.files || []);
        if (!incoming.length) return;
        if (createFiles.length + incoming.length > MAX_FILES) {
            toast.error(`Maximum ${MAX_FILES} fichiers par tâche`);
            e.target.value = '';
            return;
        }
        for (const f of incoming) {
            if (f.size > MAX_FILE_SIZE) {
                toast.error(`Fichier "${f.name}" > 10 Mo`);
                e.target.value = '';
                return;
            }
        }
        setCreateFiles(prev => [...prev, ...incoming]);
        e.target.value = '';
    };

    const removeCreateFile = (idx) => {
        setCreateFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const formatBytes = (b) => {
        if (b < 1024) return `${b} o`;
        if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
        return `${(b / 1024 / 1024).toFixed(1)} Mo`;
    };

    const submitCreate = async () => {
        if (!createDesc.trim()) return toast.error('Description requise');
        if (!createAssignee) return toast.error('Sélectionner un destinataire');
        const target = allAssignables.find(u => u.id === createAssignee);
        if (!target) return toast.error('Destinataire introuvable');
        setCreating(true);
        try {
            if (createFiles.length > 0) {
                // multipart : fichiers joints
                const fd = new FormData();
                fd.append('description', createDesc.trim());
                fd.append('assignee_id', target.id);
                fd.append('assignee_nom', target.nom);
                fd.append('assignee_type', target.type);
                fd.append('assignee_role', target.role || '');
                for (const f of createFiles) fd.append('files', f, f.name);
                await axios.post(`${API}/api/taches-effectuer/with-files`, fd, {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                await axios.post(`${API}/api/taches-effectuer`, {
                    description: createDesc.trim(),
                    assignee_id: target.id,
                    assignee_nom: target.nom,
                    assignee_type: target.type,
                    assignee_role: target.role || '',
                }, { withCredentials: true });
            }
            toast.success('Tâche créée');
            setShowCreate(false);
            setCreateDesc(''); setCreateAssignee(''); setCreateFiles([]);
            fetchItems();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setCreating(false); }
    };

    // Changement de statut (modal remarque obligatoire)
    const openStatutModal = (tache, target) => {
        setStatutModal({ tache, target });
        setStatutRemarque('');
    };
    const submitStatut = async () => {
        if (!statutRemarque.trim()) return toast.error('Remarque obligatoire');
        setStatutSaving(true);
        try {
            await axios.patch(
                `${API}/api/taches-effectuer/${statutModal.tache.id}/statut`,
                { statut: statutModal.target, remarque: statutRemarque.trim() },
                { withCredentials: true },
            );
            toast.success('Statut mis à jour');
            setStatutModal(null);
            fetchItems();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setStatutSaving(false); }
    };

    // Historique
    const openHistory = async (tache) => {
        setHistoryTache(tache);
        setHistoryLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/taches-effectuer/${tache.id}/historique`, { withCredentials: true });
            setHistoryData(data || []);
        } catch { setHistoryData([]); }
        finally { setHistoryLoading(false); }
    };

    // Suppression
    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette tâche ?')) return;
        try {
            await axios.delete(`${API}/api/taches-effectuer/${id}`, { withCredentials: true });
            toast.success('Tâche supprimée');
            fetchItems();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    // Modification description
    const startEditDescription = () => {
        if (!detailTache) return;
        setDescriptionDraft(detailTache.description || '');
        setEditingDescription(true);
    };
    const cancelEditDescription = () => {
        setEditingDescription(false);
        setDescriptionDraft('');
    };
    const saveDescription = async () => {
        if (!detailTache) return;
        const next = (descriptionDraft || '').trim();
        if (!next) { toast.error('Description obligatoire'); return; }
        if (next === (detailTache.description || '').trim()) {
            cancelEditDescription();
            return;
        }
        setSavingDescription(true);
        try {
            const { data } = await axios.patch(
                `${API}/api/taches-effectuer/${detailTache.id}/description`,
                { description: next },
                { withCredentials: true },
            );
            setDetailTache(data);
            setItems((list) => list.map((t) => (t.id === data.id ? { ...t, ...data } : t)));
            toast.success('Description mise à jour');
            cancelEditDescription();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setSavingDescription(false);
        }
    };

    // Pièces jointes (popup détail)
    const applyDetailUpdate = (data) => {
        setDetailTache(data);
        setItems((list) => list.map((t) => (t.id === data.id ? { ...t, ...data } : t)));
    };
    const handleAddDetailFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !detailTache) return;
        if ((detailTache.attachments || []).length >= MAX_FILES) { toast.error(`Maximum ${MAX_FILES} fichiers par tâche`); return; }
        if (file.size > MAX_FILE_SIZE) { toast.error(`Fichier "${file.name}" > 10 Mo`); return; }
        setUploadingFile(true);
        try {
            const fd = new FormData();
            fd.append('file', file, file.name);
            const { data } = await axios.post(`${API}/api/taches-effectuer/${detailTache.id}/files`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            applyDetailUpdate(data);
            toast.success('Fichier ajouté');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setUploadingFile(false);
        }
    };
    const handleDeleteDetailFile = async (fileId) => {
        if (!detailTache) return;
        if (!window.confirm('Supprimer ce fichier ?')) return;
        try {
            const { data } = await axios.delete(`${API}/api/taches-effectuer/${detailTache.id}/files/${fileId}`, { withCredentials: true });
            applyDetailUpdate(data);
            toast.success('Fichier supprimé');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="taches-effectuer-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
                        data-testid="taches-back-btn"
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        Retour
                    </Button>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Tâches à effectuer
                        </h1>
                        <p className="mt-1 text-slate-500">Gestion collaborative des tâches</p>
                    </div>
                </div>
                <Button
                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white gap-2"
                    onClick={() => setShowCreate(true)}
                    data-testid="add-tache-effectuer"
                >
                    <Plus size={16} /> Ajouter une nouvelle tâche
                </Button>
            </div>

            {/* Filtres */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4 flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Rechercher (description, créateur, assigné)..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="tache-effectuer-search"
                        />
                    </div>
                    <Select value={filtreCreePar} onValueChange={setFiltreCreePar}>
                        <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-cree-par">
                            <SelectValue placeholder="Créé par" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="tous">Tous les créateurs</SelectItem>
                            {users.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.nom}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={filtreAssignee} onValueChange={setFiltreAssignee}>
                        <SelectTrigger className="w-full md:w-[220px]" data-testid="filter-assignee">
                            <SelectValue placeholder="Assigné à" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="tous">Tous les destinataires</SelectItem>
                            {users.length > 0 && (
                                <>
                                    <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Utilisateurs</div>
                                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.nom}</SelectItem>)}
                                </>
                            )}
                            {demenageurs.length > 0 && (
                                <SelectItem value="demenageur">
                                    <span className="flex items-center gap-2">
                                        <Truck size={13} className="text-violet-500" /> Déménageur
                                    </span>
                                </SelectItem>
                            )}
                        </SelectContent>
                    </Select>
                    <Select value={filtreStatut} onValueChange={setFiltreStatut}>
                        <SelectTrigger className="w-full md:w-[160px]" data-testid="filter-statut">
                            <SelectValue placeholder="Statut" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="tous">Tous les statuts</SelectItem>
                            {Object.keys(STATUT_LABELS).map(s => (
                                <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </CardContent>
            </Card>

            {/* Tableau */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <ListTodo size={20} className="text-[#2e7cd6]" />
                        Liste des tâches ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">Aucune tâche</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table className="min-w-[900px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-12"></TableHead>
                                        <TableHead className="w-10"></TableHead>
                                        <TableHead>Créé par</TableHead>
                                        <TableHead>Date création</TableHead>
                                        <TableHead>Assigné à</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Modifié</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(t => {
                                        const allowed = STATUT_TRANSITIONS[t.statut] || [];
                                        const isLocked = allowed.length === 0;
                                        const Icon = t.assignee_type === 'demenageur' ? Truck : User;
                                        const iconColor = t.assignee_type === 'demenageur' ? 'text-violet-500' : 'text-slate-500';
                                        return (
                                            <TableRow key={t.id} className="hover:bg-slate-50" data-testid={`tache-row-${t.id}`}>
                                                <TableCell className="w-12">
                                                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                                                        <Icon size={16} className={iconColor} />
                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-10">
                                                    <button
                                                        onClick={() => openHistory(t)}
                                                        className="p-1 rounded-md hover:bg-blue-50 transition-colors"
                                                        title="Historique"
                                                        data-testid={`tache-history-${t.id}`}
                                                    >
                                                        <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                                                    </button>
                                                </TableCell>
                                                <TableCell className="text-sm text-slate-700">{t.cree_par_nom}</TableCell>
                                                <TableCell className="text-sm text-slate-600">{formatDate(t.created_at)}</TableCell>
                                                <TableCell>
                                                    <Badge
                                                        variant="outline"
                                                        className={`border text-xs ${ASSIGNEE_COLORS[t.assignee_role] || ASSIGNEE_COLORS.default}`}
                                                        data-testid={`tache-assignee-${t.id}`}
                                                    >
                                                        <span className="flex items-center gap-1.5">
                                                            <Icon size={11} />
                                                            <span className="font-medium">{t.assignee_nom}</span>
                                                            {t.assignee_role && (
                                                                <span className="opacity-70 text-[10px]">· {ROLE_LABELS[t.assignee_role] || t.assignee_role}</span>
                                                            )}
                                                        </span>
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <button
                                                        onClick={() => setDetailTache(t)}
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-[#2e7cd6] hover:text-white text-slate-700 text-xs font-medium transition-colors"
                                                        data-testid={`tache-detail-btn-${t.id}`}
                                                    >
                                                        <Eye size={12} />
                                                        Voir
                                                    </button>
                                                </TableCell>
                                                <TableCell>
                                                    {isLocked ? (
                                                        <Badge variant="outline" className={`text-xs border ${STATUT_COLORS[t.statut] || 'bg-slate-100'}`}>
                                                            {STATUT_LABELS[t.statut] || t.statut}
                                                        </Badge>
                                                    ) : (
                                                        <Select
                                                            value={t.statut}
                                                            onValueChange={(v) => { if (v !== t.statut) openStatutModal(t, v); }}
                                                        >
                                                            <SelectTrigger className="w-[160px] h-8" data-testid={`tache-statut-${t.id}`}>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value={t.statut}>
                                                                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUT_COLORS[t.statut]}`}>{STATUT_LABELS[t.statut]}</span>
                                                                </SelectItem>
                                                                {allowed.map(s => (
                                                                    <SelectItem key={s} value={s}>
                                                                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUT_COLORS[s]}`}>{STATUT_LABELS[s]}</span>
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-xs text-slate-500">{formatDate(t.updated_at)}</TableCell>
                                                <TableCell className="text-right">
                                                    <button
                                                        onClick={() => handleDelete(t.id)}
                                                        className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                                                        title="Supprimer"
                                                        data-testid={`tache-delete-${t.id}`}
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Modal Création */}
            <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) { setCreateDesc(''); setCreateAssignee(''); setCreateFiles([]); } }}>
                <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="tache-create-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus size={18} className="text-[#2e7cd6]" />
                            Nouvelle tâche
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div>
                            <label className="text-xs text-slate-600 mb-1 block">Description *</label>
                            <Textarea
                                value={createDesc}
                                onChange={e => setCreateDesc(e.target.value)}
                                rows={3}
                                placeholder="Décrire la tâche à effectuer..."
                                className="resize-none"
                                autoFocus
                                data-testid="tache-create-desc"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600 mb-1 block">Assigner à *</label>
                            <Select value={createAssignee} onValueChange={setCreateAssignee}>
                                <SelectTrigger data-testid="tache-create-assignee">
                                    <SelectValue placeholder="Choisir un destinataire" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.length > 0 && (
                                        <>
                                            <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Utilisateurs</div>
                                            {users.map(u => (
                                                <SelectItem key={u.id} value={u.id}>
                                                    <span className="flex items-center gap-2">
                                                        <User size={13} className="text-slate-400" /> {u.nom}
                                                        <span className="text-[10px] text-slate-400">({u.role})</span>
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                    {demenageurs.length > 0 && (
                                        <SelectItem value="demenageur">
                                            <span className="flex items-center gap-2">
                                                <Truck size={13} className="text-violet-500" /> Déménageur
                                            </span>
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Pieces jointes : photos / PDF / documents */}
                        <div>
                            <label className="text-xs text-slate-600 mb-1 flex items-center gap-1">
                                <Paperclip size={12} className="text-slate-500" />
                                Fichiers / Photos (optionnel)
                            </label>
                            <p className="text-[11px] text-slate-400 mb-2">
                                Jusqu'à {MAX_FILES} fichiers · max 10 Mo chacun
                            </p>
                            <input
                                ref={createFileInputRef}
                                type="file"
                                multiple
                                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                                className="hidden"
                                onChange={handleCreateFiles}
                                data-testid="tache-create-files-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => createFileInputRef.current?.click()}
                                className="border-dashed border-slate-300 text-slate-600 hover:bg-slate-50"
                                data-testid="tache-create-files-pick-btn"
                            >
                                <Upload size={14} className="mr-1.5" />
                                Choisir un fichier ou une photo
                            </Button>

                            {createFiles.length > 0 && (
                                <ul className="mt-2 space-y-1.5">
                                    {createFiles.map((f, i) => (
                                        <li key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5 text-sm" data-testid={`tache-create-file-${i}`}>
                                            <Paperclip size={12} className="text-slate-400 shrink-0" />
                                            <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                                            <span className="text-[10px] text-slate-400 shrink-0">{formatBytes(f.size)}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeCreateFile(i)}
                                                className="p-1 rounded hover:bg-red-50 text-red-500 shrink-0"
                                                title="Retirer"
                                            >
                                                <X size={12} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)} disabled={creating}>Annuler</Button>
                            <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={submitCreate} disabled={creating} data-testid="tache-create-submit">
                                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                                Créer
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal Changement de statut (remarque obligatoire) */}
            <Dialog open={!!statutModal} onOpenChange={(o) => { if (!o) setStatutModal(null); }}>
                <DialogContent className="sm:max-w-[480px]" data-testid="tache-statut-dialog">
                    {statutModal && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Passer en « {STATUT_LABELS[statutModal.target]} »</DialogTitle>
                            </DialogHeader>
                            <div className="pt-2">
                                <p className="text-xs text-slate-500 mb-2">Une remarque est obligatoire pour valider ce changement.</p>
                                <Textarea
                                    value={statutRemarque}
                                    onChange={e => setStatutRemarque(e.target.value)}
                                    rows={4}
                                    placeholder="Décrire le motif / la progression..."
                                    className="resize-none"
                                    autoFocus
                                    data-testid="tache-statut-remarque"
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-3">
                                <Button variant="outline" size="sm" onClick={() => setStatutModal(null)} disabled={statutSaving}>Annuler</Button>
                                <Button
                                    size="sm"
                                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                    onClick={submitStatut}
                                    disabled={statutSaving || !statutRemarque.trim()}
                                    data-testid="tache-statut-confirm"
                                >
                                    {statutSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                                    Valider
                                </Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal Historique */}
            <Dialog open={!!historyTache} onOpenChange={(o) => { if (!o) { setHistoryTache(null); setHistoryData([]); } }}>
                <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto" data-testid="tache-history-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                            Historique de la tâche
                        </DialogTitle>
                    </DialogHeader>
                    {historyLoading ? (
                        <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#2e7cd6]" /></div>
                    ) : historyData.length === 0 ? (
                        <div className="text-center py-10 text-slate-400 text-sm">Aucun historique</div>
                    ) : (
                        <div className="relative pl-6 mt-2">
                            <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200" />
                            <div className="space-y-5">
                                {historyData.map((entry, idx) => (
                                    <div key={idx} className="relative flex items-start gap-3" data-testid={`tache-history-entry-${idx}`}>
                                        <div className={`absolute -left-6 top-1 w-3 h-3 rounded-full border-2 ${idx === 0 ? 'bg-[#2e7cd6] border-[#2e7cd6]' : 'bg-white border-slate-300'}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {entry.ancien_statut && (
                                                    <>
                                                        <Badge variant="outline" className={`${STATUT_COLORS[entry.ancien_statut] || ''} border text-xs`}>
                                                            {STATUT_LABELS[entry.ancien_statut] || entry.ancien_statut}
                                                        </Badge>
                                                        <ArrowRight size={14} className="text-slate-400 shrink-0" />
                                                    </>
                                                )}
                                                <Badge variant="outline" className={`${STATUT_COLORS[entry.nouveau_statut] || ''} border text-xs`}>
                                                    {STATUT_LABELS[entry.nouveau_statut] || entry.nouveau_statut}
                                                </Badge>
                                                <span className="text-slate-300 mx-1">|</span>
                                                <Clock size={12} className="text-slate-400 shrink-0" />
                                                <span className="text-xs text-slate-400">{formatDate(entry.changed_at)}</span>
                                                <span className="text-slate-300">|</span>
                                                <span className="text-xs text-slate-400">{entry.changed_by || 'Système'}</span>
                                            </div>
                                            {entry.remarque && (
                                                <div className="mt-2 bg-amber-50 border-l-2 border-amber-400 rounded-r px-3 py-2">
                                                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{entry.remarque}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            {/* Modal Détail tâche */}
            <Dialog open={!!detailTache} onOpenChange={(o) => { if (!o) { setDetailTache(null); cancelEditDescription(); } }}>
                <DialogContent className="sm:max-w-[600px]" data-testid="tache-detail-dialog">
                    {detailTache && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Eye size={18} className="text-[#2e7cd6]" />
                                    Détail de la tâche
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className={`border text-xs ${STATUT_COLORS[detailTache.statut]}`}>
                                        {STATUT_LABELS[detailTache.statut]}
                                    </Badge>
                                    <Badge variant="outline" className={`border text-xs ${ASSIGNEE_COLORS[detailTache.assignee_role] || ASSIGNEE_COLORS.default}`}>
                                        <span className="flex items-center gap-1.5">
                                            {detailTache.assignee_type === 'demenageur' ? <Truck size={11} /> : <User size={11} />}
                                            {detailTache.assignee_nom}
                                            {detailTache.assignee_role && (
                                                <span className="opacity-70 text-[10px]">· {ROLE_LABELS[detailTache.assignee_role] || detailTache.assignee_role}</span>
                                            )}
                                        </span>
                                    </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-xs text-slate-500">
                                    <div>
                                        <p className="uppercase tracking-wide font-semibold text-[10px] text-slate-400">Créé par</p>
                                        <p className="text-slate-700 font-medium mt-0.5">{detailTache.cree_par_nom}</p>
                                    </div>
                                    <div>
                                        <p className="uppercase tracking-wide font-semibold text-[10px] text-slate-400">Créé le</p>
                                        <p className="text-slate-700 font-medium mt-0.5">{formatDate(detailTache.created_at)}</p>
                                    </div>
                                    <div>
                                        <p className="uppercase tracking-wide font-semibold text-[10px] text-slate-400">Modifié le</p>
                                        <p className="text-slate-700 font-medium mt-0.5">{formatDate(detailTache.updated_at)}</p>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs uppercase tracking-wide font-semibold text-slate-400">Description</p>
                                        {!editingDescription && (
                                            <button
                                                type="button"
                                                onClick={startEditDescription}
                                                className="flex items-center gap-1 text-xs text-[#2e7cd6] hover:text-[#2568b8] font-medium"
                                                data-testid="tache-edit-description-btn"
                                            >
                                                <Pencil size={12} />
                                                Modifier
                                            </button>
                                        )}
                                    </div>
                                    {editingDescription ? (
                                        <div className="space-y-2">
                                            <Textarea
                                                value={descriptionDraft}
                                                onChange={(e) => setDescriptionDraft(e.target.value)}
                                                rows={4}
                                                className="text-sm"
                                                data-testid="tache-edit-description-textarea"
                                                autoFocus
                                            />
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={cancelEditDescription}
                                                    disabled={savingDescription}
                                                    data-testid="tache-edit-description-cancel-btn"
                                                >
                                                    Annuler
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white gap-1"
                                                    onClick={saveDescription}
                                                    disabled={savingDescription}
                                                    data-testid="tache-edit-description-save-btn"
                                                >
                                                    {savingDescription ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                                    Enregistrer
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{detailTache.description}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Pièces jointes */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs uppercase tracking-wide font-semibold text-slate-400 flex items-center gap-1">
                                            <Paperclip size={12} /> Pièces jointes ({(detailTache.attachments || []).length})
                                        </p>
                                        <input
                                            ref={detailFileInputRef}
                                            type="file"
                                            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                                            className="hidden"
                                            onChange={handleAddDetailFile}
                                            data-testid="tache-detail-file-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => detailFileInputRef.current?.click()}
                                            disabled={uploadingFile}
                                            className="flex items-center gap-1 text-xs text-[#2e7cd6] hover:text-[#2568b8] font-medium disabled:opacity-50"
                                            data-testid="tache-add-file-btn"
                                        >
                                            {uploadingFile ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                                            Ajouter un fichier
                                        </button>
                                    </div>
                                    {(detailTache.attachments || []).length === 0 ? (
                                        <p className="text-xs italic text-slate-400">Aucun fichier</p>
                                    ) : (
                                        <ul className="space-y-1.5">
                                            {detailTache.attachments.map((att) => {
                                                const isImage = (att.content_type || '').startsWith('image/');
                                                const url = `${API}/api/taches-effectuer/${detailTache.id}/files/${att.id}`;
                                                return (
                                                    <li key={att.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 hover:border-[#2e7cd6] transition-colors" data-testid={`tache-attachment-${att.id}`}>
                                                        {isImage ? <FileText size={14} className="text-blue-500 shrink-0" /> : <Paperclip size={14} className="text-slate-400 shrink-0" />}
                                                        <span className="flex-1 truncate text-sm text-slate-700">{att.filename}</span>
                                                        <span className="text-[10px] text-slate-400 shrink-0">{att.size ? `${(att.size / 1024).toFixed(1)} Ko` : ''}</span>
                                                        <a
                                                            href={url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-1.5 rounded hover:bg-blue-50 text-[#2e7cd6] shrink-0"
                                                            title="Ouvrir / télécharger"
                                                            data-testid={`tache-attachment-download-${att.id}`}
                                                        >
                                                            <Download size={14} />
                                                        </a>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteDetailFile(att.id)}
                                                            className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 shrink-0"
                                                            title="Supprimer le fichier"
                                                            data-testid={`tache-attachment-delete-${att.id}`}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
